import React from 'react';
import { Field } from '@api';
import { EditDocumentModal } from './EditDocumentModal';
import { Modal } from './seachpage-modal';
import { AuditEvent } from './seachpage-types';

interface DocumentModalsProps {
  // Edit Document
  editDoc: any | null;
  editValues: Record<string, any>;
  savingEdit: boolean;
  editError: string | null;
  documentFields: Field[]; 
  onEditValuesChange: (values: Record<string, any>) => void;
  onEditCancel: () => void;
  onEditSave: () => void;
  
  // Audit
  auditDoc: any | null;
  audits: AuditEvent[];
  loadingAudits: boolean;
  auditError: string | null;
  onAuditClose: () => void;
  
  // Metadata (rows)
  metadataOpen: boolean;
  metadataRows: Array<Record<string, any>>;
  onMetadataClose: () => void;
}

export const DocumentModals: React.FC<DocumentModalsProps> = ({
  editDoc,
  editValues,
  savingEdit,
  editError,
  documentFields,
  onEditValuesChange,
  onEditCancel,
  onEditSave,
  auditDoc,
  audits,
  loadingAudits,
  auditError,
  onAuditClose,
  metadataOpen,
  metadataRows,
  onMetadataClose
}) => {
  return (
    <>
      {/* Edit Document Modal */}
      {editDoc && (
        <EditDocumentModal
          doc={editDoc}
          documentFields={documentFields}
          values={editValues}
          onChange={onEditValuesChange}
          onCancel={onEditCancel}
          onSave={onEditSave}
          saving={savingEdit}
          error={editError}
        />
      )}

      {/* Audit Modal */}
      {auditDoc && (
        <Modal title={`Audit - Document #${auditDoc.id}`} onClose={onAuditClose}>
          {auditError && <div style={{ color: 'red', marginBottom: 8 }}>{auditError}</div>}
          {loadingAudits ? (
            <div>Loading audit...</div>
          ) : audits.length ? (
            <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
              {audits.map((a, i) => (
                <li key={i} style={{ background: '#f7fafd', border: '1px solid #e0e6ed', borderRadius: 6, padding: 8 }}>
                  <div style={{ fontSize: 12, color: '#555', marginBottom: 4 }}>
                    {new Date(a.date).toLocaleString()} {a.name && ' - ' + a.name}
                  </div>
                  <div style={{ whiteSpace: 'pre-wrap' }}>{a.text}</div>
                </li>
              ))}
            </ul>
          ) : (
            <div>No audit events.</div>
          )}
        </Modal>
      )}

      {/* Metadata Modal */}
      {metadataOpen && (
        <Modal title={`Metadata`} onClose={onMetadataClose}>
          {(() => {
            if (!metadataRows || metadataRows.length === 0) return <div>No metadata.</div>;

            // Build columns from union of keys across all rows
            const columnSet = new Set<string>();
            for (const r of metadataRows) {
              Object.keys(r || {}).forEach(k => columnSet.add(k));
            }
            const columns = Array.from(columnSet).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));

            const containerStyle: React.CSSProperties = {
              display: 'flex',
              flexDirection: 'column',
              gap: 8
            };

            const tableWrapStyle: React.CSSProperties = {
              maxHeight: '70vh',
              overflow: 'auto',
              border: '1px solid #e5e7eb',
              borderRadius: 6
            };

            const tableStyle: React.CSSProperties = {
              width: '100%',
              borderCollapse: 'separate',
              borderSpacing: 0
            };

            const thStyle: React.CSSProperties = {
              position: 'sticky',
              top: 0,
              background: '#f8fafc',
              textAlign: 'left',
              fontWeight: 600,
              fontSize: 12,
              color: '#374151',
              padding: '8px 10px',
              borderBottom: '1px solid #e5e7eb',
              zIndex: 1
            };

            const tdBase: React.CSSProperties = {
              padding: '6px 10px',
              borderBottom: '1px solid #f1f5f9',
              verticalAlign: 'top',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word'
            };

            const summary = `${metadataRows.length} row${metadataRows.length === 1 ? '' : 's'} � ${columns.length} column${columns.length === 1 ? '' : 's'}`;

            const formatVal = (val: any) => {
              if (val == null) return '';
              if (typeof val === 'number') return val.toString();
              if (typeof val === 'boolean') return val ? 'true' : 'false';
              if (Array.isArray(val)) return val.map(v => (typeof v === 'object' ? JSON.stringify(v) : String(v))).join(', ');
              if (typeof val === 'object') return JSON.stringify(val);
              return String(val);
            };

            const isNumeric = (val: any) => typeof val === 'number' || (typeof val === 'string' && /^-?\d+(?:[.,]\d+)?$/.test(val));

            return (
              <div style={containerStyle}>
                <div style={{ fontSize: 12, color: '#6b7280' }}>{summary}</div>
                <div style={tableWrapStyle}>
                  <table style={tableStyle}>
                    <thead>
                      <tr>
                        {columns.map(col => (
                          <th key={col} style={thStyle}>{col}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {metadataRows.map((row, rIdx) => (
                        <tr key={rIdx} style={{ background: rIdx % 2 === 0 ? '#ffffff' : '#fbfdff' }}>
                          {columns.map(col => {
                            const raw = (row as any)[col];
                            const txt = formatVal(raw);
                            const isNum = isNumeric(raw);
                            return (
                              <td key={col} style={{ ...tdBase, textAlign: isNum ? 'right' as const : 'left' }}>
                                {txt}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })()}
        </Modal>
      )}
    </>
  );
};